/** Shared table controls driven by feature-owned values, labels and API mappings. */
import { CalendarDays, ListFilter, type LucideIcon, RotateCcw, Trash2, X } from "lucide-react";
import { parseISO } from "date-fns";
import { Fragment, type ReactElement, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { SearchInput } from "@/shared/components/filter-bar";
import { TableFacet, type TableFacetOption } from "@/shared/components/table-facet";
import { TableSortMenu } from "@/shared/components/table-sort-menu";
import { Button } from "@/shared/ui/button";
import { Calendar } from "@/shared/ui/calendar";
import { Input, Select } from "@/shared/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";

/** Values supported by the two server-backed table pilots. */
export type TableFilterValue = string | boolean | undefined;

interface FilterBase {
  id: string;
  label: string;
  value: TableFilterValue;
  active?: boolean;
  prominent?: boolean;
  icon?: LucideIcon;
  removeLabel?: string;
  formatValue?: (value: TableFilterValue) => string;
  onChange: (value: TableFilterValue) => void;
  onRemove: () => void;
}

/** A supported field, not an arbitrary ORM field or a client-side row predicate. */
export type TableFilterDefinition = FilterBase &
  (
    | { kind: "select" | "multi"; options: TableFacetOption[] }
    | { kind: "boolean"; options?: TableFacetOption[] }
    | { kind: "date"; boundary: "start" | "end" }
    | { kind: "text" }
    | { kind: "custom"; renderEditor: (trigger?: ReactElement) => ReactElement }
  );

function FilterValue({
  definition,
  trigger,
}: {
  definition: TableFilterDefinition;
  trigger?: ReactElement;
}): ReactElement {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  if (definition.kind === "custom") return definition.renderEditor(trigger);
  if (definition.kind === "text" || definition.kind === "date") {
    const date = typeof definition.value === "string" ? parseISO(definition.value) : undefined;
    const selected = date && !Number.isNaN(date.getTime()) ? date : undefined;
    return (
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (next) setText(typeof definition.value === "string" ? definition.value : "");
          setOpen(next);
        }}
      >
        <PopoverTrigger asChild>
          {trigger ?? (
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
              aria-label={definition.label}
            >
              {definition.kind === "date" ? (
                <CalendarDays className="size-4 shrink-0" aria-hidden />
              ) : null}
              {definition.kind === "date"
                ? selected
                  ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(selected)
                  : t("tablePilot.chooseDate")
                : String(definition.value ?? t("app.all"))}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-72" aria-label={definition.label}>
          {definition.kind === "date" ? (
            <Calendar
              mode="single"
              selected={selected}
              defaultMonth={selected}
              onSelect={(next) => {
                if (next) {
                  if (definition.boundary === "end") next.setHours(23, 59, 59, 999);
                  else next.setHours(0, 0, 0, 0);
                }
                definition.onChange(next?.toISOString());
                setOpen(false);
              }}
            />
          ) : (
            <form
              className="space-y-3 p-3"
              onSubmit={(event) => {
                event.preventDefault();
                definition.onChange(text || undefined);
                setOpen(false);
              }}
            >
              <Input
                type="search"
                name="filter-value"
                autoComplete="off"
                aria-label={definition.label}
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
              <Button type="submit" size="sm" className="w-full">
                {t("app.filter")}
              </Button>
            </form>
          )}
        </PopoverContent>
      </Popover>
    );
  }
  return (
    <TableFacet
      label={definition.label}
      showLabel={false}
      trigger={trigger}
      options={
        definition.options ?? [
          { value: "true", label: t("app.yes") },
          { value: "false", label: t("app.no") },
        ]
      }
      multiple={definition.kind === "multi"}
      value={definition.value === undefined ? undefined : String(definition.value)}
      onChange={(value) =>
        definition.onChange(
          definition.kind === "boolean"
            ? value === undefined
              ? undefined
              : value === "true"
            : value,
        )
      }
    />
  );
}

/** Render search, facets, editable summaries, filter rules and server sorting. */
export function TableToolbar({
  filters,
  search,
  searchPlaceholder,
  searchLabel,
  searchDescription,
  onSearchChange,
  sorting,
  sortFields,
  onSortingChange,
  onReset,
  validationMessage,
  actions,
}: {
  filters: TableFilterDefinition[];
  search?: string;
  searchPlaceholder?: string;
  searchLabel?: string;
  searchDescription?: string;
  onSearchChange?: (value: string | undefined) => void;
  sorting?: string;
  sortFields?: TableFacetOption[];
  onSortingChange?: (value: string | undefined) => void;
  onReset: () => void;
  validationMessage?: string;
  actions?: ReactElement | null;
}): ReactElement {
  const { t, i18n } = useTranslation();
  const [extraFields, setExtraFields] = useState<string[]>([]);
  const addRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const active = filters.filter(
    (filter) => filter.active ?? (filter.value !== undefined && filter.value !== ""),
  );
  const rows = filters.filter(
    (filter) => active.includes(filter) || extraFields.includes(filter.id),
  );
  const available = filters.filter((filter) => !rows.includes(filter));
  const reset = (): void => {
    setExtraFields([]);
    onReset();
  };
  const remove = (filter: TableFilterDefinition): void => {
    filter.onRemove();
    setExtraFields((current) => current.filter((id) => id !== filter.id));
    (
      addRef.current?.querySelector("select") ?? triggerRef.current?.querySelector("button")
    )?.focus();
  };
  const valueLabel = (filter: TableFilterDefinition): string => {
    if (filter.formatValue) return filter.formatValue(filter.value);
    if (typeof filter.value === "boolean") return t(filter.value ? "app.yes" : "app.no");
    if (!filter.value) return "";
    if (filter.kind === "date") {
      const date = parseISO(filter.value);
      return Number.isNaN(date.getTime())
        ? filter.value
        : new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(date);
    }
    if (filter.kind === "select" || filter.kind === "multi") {
      const values = filter.kind === "multi" ? filter.value.split(",") : [filter.value];
      return values
        .map((value) => filter.options.find((option) => option.value === value)?.label ?? value)
        .join(", ");
    }
    return filter.value;
  };
  return (
    <section className="mb-3 space-y-2" aria-label={t("app.filters")}>
      <div className="flex flex-wrap items-center gap-1.5">
        {onSearchChange ? (
          <div ref={searchRef} className="w-full sm:w-auto">
            <SearchInput
              value={search}
              onChange={onSearchChange}
              placeholder={searchPlaceholder}
              label={searchLabel}
              description={searchDescription}
            />
          </div>
        ) : null}
        {filters
          .filter((filter) => filter.prominent)
          .map((filter) =>
            filter.kind === "boolean" ? (
              <Button
                key={filter.id}
                variant={filter.value === true ? "secondary" : "outline"}
                size="sm"
                aria-pressed={filter.value === true}
                onClick={() => (filter.value === true ? filter.onRemove() : filter.onChange(true))}
              >
                {filter.icon ? <filter.icon className="size-3.5" aria-hidden /> : null}
                {filter.label}
              </Button>
            ) : filter.kind === "select" || filter.kind === "multi" ? (
              <TableFacet
                key={filter.id}
                label={filter.label}
                options={filter.options}
                multiple={filter.kind === "multi"}
                value={typeof filter.value === "string" ? filter.value : undefined}
                onChange={filter.onChange}
              />
            ) : filter.kind === "custom" ? (
              <Fragment key={filter.id}>{filter.renderEditor()}</Fragment>
            ) : null,
          )}
        {filters.length ? (
          <Popover>
            <span ref={triggerRef}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  aria-label={t("tablePilot.editFilters")}
                >
                  <ListFilter className="size-4" aria-hidden />
                  {t("app.filters")}
                  {active.length ? (
                    <span className="rounded bg-muted px-1.5 tabular-nums">{active.length}</span>
                  ) : null}
                </Button>
              </PopoverTrigger>
            </span>
            <PopoverContent
              className="flex w-[42rem] flex-col overflow-hidden"
              aria-label={t("tablePilot.editFilters")}
            >
              <div className="flex shrink-0 items-center justify-between gap-3 border-b p-3">
                <h2 className="text-sm font-semibold">{t("app.filters")}</h2>
                <span className="text-xs text-muted-foreground">{t("tablePilot.matchAll")}</span>
              </div>
              <div className="min-h-0 overflow-y-auto p-3">
                <div className="space-y-3">
                  {rows.map((filter) => (
                    <div
                      key={filter.id}
                      className="grid grid-cols-[minmax(0,1fr)_2rem] items-center gap-2 border-b pb-3 last:border-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_2rem]"
                      data-testid="table-filter-row"
                    >
                      <div className="min-w-0 text-sm">
                        <span className="font-medium">{filter.label}</span>
                        <span className="ms-2 text-xs text-muted-foreground">
                          {t(
                            filter.kind === "multi"
                              ? "tablePilot.anyOf"
                              : filter.kind === "date"
                                ? filter.boundary === "start"
                                  ? "tablePilot.onOrAfter"
                                  : "tablePilot.onOrBefore"
                                : "tablePilot.is",
                          )}
                        </span>
                      </div>
                      <div className="col-start-1 row-start-2 min-w-0 [&>button]:w-full [&>button]:justify-start sm:col-start-2 sm:row-start-1">
                        <FilterValue definition={filter} />
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="col-start-2 row-start-1 size-8 sm:col-start-3"
                        title={`${t("app.remove")}: ${filter.label}`}
                        aria-label={`${t("app.remove")}: ${filter.label}`}
                        onClick={() => remove(filter)}
                      >
                        <Trash2 className="size-4" aria-hidden />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              <div ref={addRef} className="flex shrink-0 flex-wrap items-center gap-2 border-t p-3">
                <Select
                  className="min-w-0 flex-1 sm:w-auto"
                  aria-label={t("tablePilot.addFilter")}
                  value=""
                  disabled={!available.length}
                  onChange={(event) =>
                    setExtraFields((current) => [...current, event.target.value])
                  }
                >
                  <option value="">{t("tablePilot.addFilter")}</option>
                  {available.map((filter) => (
                    <option key={filter.id} value={filter.id}>
                      {filter.label}
                    </option>
                  ))}
                </Select>
                <Button variant="ghost" size="sm" onClick={reset}>
                  <RotateCcw className="size-3.5" aria-hidden />
                  {t("tablePilot.resetFilters")}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        ) : null}
        {(sortFields?.length && onSortingChange) || actions ? (
          <div className="ms-auto flex items-center gap-2">
            {sortFields?.length && onSortingChange ? (
              <TableSortMenu fields={sortFields} value={sorting} onChange={onSortingChange} />
            ) : null}
            {actions}
          </div>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        {search ? (
          <span className="inline-flex max-w-full items-center rounded-sm border bg-card">
            <button
              type="button"
              className="min-w-0 break-words rounded-s-sm px-2 py-0.5 text-start hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring [@media(pointer:coarse)]:min-h-11"
              aria-label={t("tablePilot.editFilter", { label: t("app.search") })}
              onClick={() => searchRef.current?.querySelector("input")?.focus()}
            >
              {t("app.search")}: {search}
            </button>
            <button
              type="button"
              className="shrink-0 rounded-e-sm p-1 hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
              aria-label={`${t("app.remove")}: ${t("app.search")}: ${search}`}
              onClick={() => onSearchChange?.(undefined)}
            >
              <X className="size-3" aria-hidden />
            </button>
          </span>
        ) : null}
        {active.map((filter) => (
          <span
            key={filter.id}
            className="inline-flex max-w-full items-center rounded-sm border bg-card"
          >
            <FilterValue
              definition={filter}
              trigger={
                <button
                  type="button"
                  className="min-w-0 break-words rounded-s-sm px-2 py-0.5 text-start hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [@media(pointer:coarse)]:min-h-11"
                  aria-label={t("tablePilot.editFilter", { label: filter.label })}
                >
                  <span className="text-muted-foreground">{filter.label}:</span>{" "}
                  {valueLabel(filter)}
                </button>
              }
            />
            <button
              type="button"
              className="shrink-0 rounded-e-sm p-1 hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
              aria-label={`${t("app.remove")}: ${filter.removeLabel ?? `${filter.label}: ${valueLabel(filter)}`}`}
              onClick={() => remove(filter)}
            >
              <X className="size-3" aria-hidden />
            </button>
          </span>
        ))}
        {search || active.length ? (
          <Button variant="ghost" size="sm" onClick={reset}>
            {t("tablePilot.resetFilters")}
          </Button>
        ) : null}
      </div>
      {validationMessage ? (
        <p role="alert" className="text-sm text-destructive">
          {validationMessage}
        </p>
      ) : null}
    </section>
  );
}
